import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Button as MuiButton,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FaQrcode } from 'react-icons/fa';
import { Button } from '../../../Form/Button/button.scomponent';
import { Input } from '../../../Form/Input/input.component';
import { InternalCodeDrawer } from '../../../Dialog/variants/internal-code/internal-code-dialog.component';
import {
    IGetMaskOptionsResponse,
    IGetVariantsByProductResponse,
    IMaskCodeDetails,
    INuvemShopVariationMaskSelection,
} from '../../../../Services/api/products/products.interface';

export interface INuvemShopVariationsModalProps {
    onClose: () => void;
    onConfirm: (selections: INuvemShopVariationMaskSelection[]) => void;
    channel: 'B2B' | 'B2C';
    variants: IGetVariantsByProductResponse[] | null;
    maskOptions: IGetMaskOptionsResponse;
    initialSelections?: INuvemShopVariationMaskSelection[];
}

const channelTitle: Record<INuvemShopVariationsModalProps['channel'], string> = {
    B2B: 'B2B (EMPO)',
    B2C: 'B2C (PAPELLO)',
};

function buildInitialMaps(initialSelections: INuvemShopVariationMaskSelection[] | undefined) {
    const selected: Record<number, boolean> = {};
    const masks: Record<number, IMaskCodeDetails> = {};
    for (const item of initialSelections ?? []) {
        selected[item.variantId] = true;
        masks[item.variantId] = item.maskSchema;
    }
    return { selected, masks };
}

const NuvemShopVariationsModal: FC<INuvemShopVariationsModalProps> = ({
    onClose,
    onConfirm,
    channel,
    variants,
    maskOptions,
    initialSelections,
}) => {
    const [{ selected, masks }, setRowState] = useState(() => buildInitialMaps(initialSelections));
    const [maskDrawerOpen, setMaskDrawerOpen] = useState(false);
    const [activeVariantId, setActiveVariantId] = useState<number | null>(null);

    const hasAny = (variants?.length ?? 0) > 0;
    const title = channelTitle[channel];

    const toggleRow = useCallback((variantId: number, checked: boolean) => {
        setRowState((prev) => {
            const nextSelected = { ...prev.selected };
            const nextMasks = { ...prev.masks };
            if (checked) {
                nextSelected[variantId] = true;
            } else {
                delete nextSelected[variantId];
                delete nextMasks[variantId];
            }
            return { selected: nextSelected, masks: nextMasks };
        });
    }, []);

    const selectAllCheckboxState = useMemo(() => {
        const list = variants ?? [];
        if (list.length === 0) {
            return { all: false, some: false };
        }
        const selectedCount = list.filter((v) => selected[v.variantId]).length;
        return {
            all: selectedCount === list.length,
            some: selectedCount > 0 && selectedCount < list.length,
        };
    }, [variants, selected]);

    const toggleSelectAll = useCallback(
        (checked: boolean) => {
            const list = variants ?? [];
            if (list.length === 0) return;
            setRowState((prev) => {
                if (!checked) {
                    return { selected: {}, masks: {} };
                }
                const nextSelected: Record<number, boolean> = {};
                for (const v of list) {
                    nextSelected[v.variantId] = true;
                }
                const nextMasks: Record<number, IMaskCodeDetails> = {};
                for (const v of list) {
                    const id = v.variantId;
                    if (prev.masks[id]) {
                        nextMasks[id] = prev.masks[id];
                    }
                }
                return { selected: nextSelected, masks: nextMasks };
            });
        },
        [variants],
    );

    const openMaskForVariant = useCallback((variantId: number) => {
        setActiveVariantId(variantId);
        setMaskDrawerOpen(true);
    }, []);

    const handleMaskSaved = useCallback((schema: IMaskCodeDetails) => {
        if (activeVariantId == null) return;
        setRowState((prev) => ({
            ...prev,
            masks: { ...prev.masks, [activeVariantId]: schema },
        }));
        setMaskDrawerOpen(false);
        setActiveVariantId(null);
    }, [activeVariantId]);

    const closeMaskDrawer = useCallback(() => {
        setMaskDrawerOpen(false);
        setActiveVariantId(null);
    }, []);

    const selectedEntries = useMemo(() => {
        if (!variants?.length) return [];
        return variants
            .filter((v) => selected[v.variantId])
            .map((v) => ({
                variant: v,
                mask: masks[v.variantId],
            }));
    }, [variants, selected, masks]);

    const emptySelection = selectedEntries.length === 0;
    const allSelectedHaveMask = useMemo(
        () =>
            selectedEntries.every((e) => Boolean(e.mask?.generatedMaskName?.trim())),
        [selectedEntries],
    );
    const canConfirm = emptySelection || allSelectedHaveMask;

    const handleConfirm = () => {
        if (emptySelection) {
            onConfirm([]);
            return;
        }
        const list: INuvemShopVariationMaskSelection[] = selectedEntries
            .filter((e) => e.mask?.generatedMaskName?.trim())
            .map((e) => ({
                variantId: e.variant.variantId,
                name: e.variant.name,
                code: e.variant.code,
                maskSchema: e.mask as IMaskCodeDetails,
            }));

        onConfirm(list);
    };

    const activeMaskSchema =
        activeVariantId != null ? masks[activeVariantId] ?? {} : {};

    return (
        <>
            <Dialog
                open
                keepMounted
                fullScreen
                onClose={onClose}
                aria-describedby="nuvemshop-variations-dialog"
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Variações NuvemShop — {title}</Typography>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {!hasAny ? (
                        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                            <Typography color="textSecondary">
                                Nenhuma variação para esta loja.
                            </Typography>
                        </Box>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={selectAllCheckboxState.some}
                                            checked={selectAllCheckboxState.all}
                                            onChange={(_, c) => toggleSelectAll(c)}
                                            inputProps={{
                                                'aria-label': 'Selecionar ou desmarcar todas as variações',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>Nome</TableCell>
                                    <TableCell>Código</TableCell>
                                    <TableCell align="right">ID variação</TableCell>
                                    <TableCell sx={{ minWidth: 280 }}>Código interno (máscara)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {variants!.map((row, index) => {
                                    const isChecked = Boolean(selected[row.variantId]);
                                    const rowMask = masks[row.variantId];
                                    return (
                                        <TableRow key={`${channel}-${row.variantId}-${index}`}>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={isChecked}
                                                    onChange={(_, c) => toggleRow(row.variantId, c)}
                                                />
                                            </TableCell>
                                            <TableCell>{row.name}</TableCell>
                                            <TableCell>{row.code ?? '—'}</TableCell>
                                            <TableCell align="right">{row.variantId}</TableCell>
                                            <TableCell>
                                                {isChecked ? (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            alignItems: 'flex-start',
                                                            width: '100%',
                                                        }}
                                                    >
                                                        <Input
                                                            
                                                            placeholder="Monte o código pela máscara"
                                                            disabled
                                                            sx={{ flex: '1 1 auto', minWidth: 0 }}
                                                            value={rowMask?.generatedMaskName ?? ''}
                                                            onChange={() => {}}
                                                        />
                                                        <Button
                                                            type="button"
                                                            size="small"
                                                            onClick={() => openMaskForVariant(row.variantId)}
                                                            sx={{
                                                               
                                                                width: 50,
                                                                height: 52,
                                                                flex: '0 0 auto',
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            <FaQrcode size={22} style={{ marginTop: 2 }} color="#fff" />
                                                        </Button>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        —
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}

                    {selectedEntries.length > 0 && (
                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 2,
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                Resumo — variações selecionadas
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                {selectedEntries.map(({ variant, mask }) => (
                                    <li key={variant.variantId}>
                                        <Typography variant="body2">
                                            {variant.code ?? '—'}
                                            {' — '}
                                            {mask?.generatedMaskName?.trim()
                                                ? mask.generatedMaskName
                                                : 'máscara pendente'}
                                        </Typography>
                                    </li>
                                ))}
                            </Box>
                            {!emptySelection && !allSelectedHaveMask && (
                                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                                    Defina a máscara (ícone QR) em cada linha selecionada antes de confirmar.
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <MuiButton onClick={onClose} color="inherit">
                        Cancelar
                    </MuiButton>
                    <Button type="button" disabled={!canConfirm} onClick={handleConfirm}>
                        Confirmar seleção
                    </Button>
                </DialogActions>
            </Dialog>

            <InternalCodeDrawer
                handleSelect={handleMaskSaved}
                open={maskDrawerOpen}
                onToggle={closeMaskDrawer}
                title="Código Interno"
                code={activeMaskSchema?.generatedMaskName || ''}
                initialMaskSchema={activeMaskSchema}
                options={maskOptions}
            />
        </>
    );
};

export default NuvemShopVariationsModal;
